import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight, CalendarDays } from "lucide-react";
import { useI18n } from "@/lib/i18n";
import { BRAND } from "@/lib/brand";
import { useMobilI18n } from "@/lib/mobilI18n";

const P = BRAND.mobilPhotos;

function PhotoCollage({ m }) {
  return (
    <div className="relative h-[360px] sm:h-[440px] lg:h-full lg:min-h-[480px]" data-testid="offsite-collage">
      <motion.div initial={{ opacity: 0, scale: 0.96 }} whileInView={{ opacity: 1, scale: 1 }} viewport={{ once: true }} transition={{ duration: 0.9 }}
        className="absolute inset-0 right-[22%] rounded-2xl overflow-hidden">
        <img src={P.hero} alt={m.name} loading="lazy" className="w-full h-full object-cover" />
      </motion.div>
      <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.8, delay: 0.2 }}
        className="absolute top-8 right-0 w-[42%] aspect-[3/4] rounded-2xl overflow-hidden border-4 border-[#2e5949] shadow-[0_20px_40px_rgb(0,0,0,0.3)]">
        <img src={P.homeAccent} alt={m.name} loading="lazy" className="w-full h-full object-cover" />
      </motion.div>
      <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.8, delay: 0.35 }}
        className="absolute bottom-6 right-[6%] w-[36%] aspect-square rounded-2xl overflow-hidden border-4 border-[#2e5949] shadow-[0_20px_40px_rgb(0,0,0,0.3)]">
        <img src={P.gallery[0]} alt={m.name} loading="lazy" className="w-full h-full object-cover" />
      </motion.div>
      <div className="absolute bottom-6 left-5 bg-[#fdfcf9] text-[#2e5949] rounded-2xl px-5 py-4 shadow-[0_15px_35px_rgb(0,0,0,0.25)]">
        <p className="text-[10px] uppercase tracking-[0.3em] text-[#C9A24B]">AURA</p>
        <p className="font-display text-xl leading-none mt-1">{m.badge}</p>
      </div>
    </div>
  );
}

export default function OffsiteSection() {
  const { t } = useI18n();
  const { m } = useMobilI18n();
  return (
    <section id="offsite" data-testid="offsite-section" className="relative py-20 md:py-28 px-6 md:px-12 lg:px-24 bg-[#fdfcf9]">
      <motion.div
        initial={{ opacity: 0, y: 24 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-100px" }}
        transition={{ duration: 0.7 }}
        className="max-w-7xl mx-auto rounded-3xl bg-gradient-to-br from-[#3e664c] to-[#2e5949] text-[#fdfcf9] p-5 md:p-8 shadow-[0_30px_60px_rgb(31,58,48,0.25)] overflow-hidden relative"
      >
        <div className="absolute -top-20 -right-20 w-72 h-72 rounded-full bg-[#E2C9A0]/15 blur-3xl pointer-events-none" />
        <div className="relative grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-14 items-stretch">
          <PhotoCollage m={m} />
          <div className="py-4 md:py-8 lg:pr-6 flex flex-col justify-center">
            <p className="uppercase tracking-[0.32em] text-[11px] text-[#E2C9A0] mb-5 font-medium">{t.offsite.eyebrow}</p>
            <h2 className="font-display text-3xl md:text-4xl lg:text-5xl leading-[1.1] tracking-[-0.01em]">{t.offsite.title}</h2>
            <p className="mt-6 text-[#fdfcf9]/85 text-base leading-relaxed max-w-xl">{t.offsite.desc}</p>
            <ul className="mt-8 flex flex-wrap gap-2" data-testid="offsite-list">
              {t.offsite.items.map((item, i) => (
                <li key={item} data-testid={`offsite-item-${i}`}
                  className="px-3.5 py-1.5 rounded-full border border-[#E2C9A0]/35 bg-[#fdfcf9]/[0.06] text-xs md:text-sm text-[#fdfcf9]/95 hover:bg-[#E2C9A0]/20 transition-colors">
                  {item}
                </li>
              ))}
            </ul>
            <div className="mt-10 flex flex-wrap items-center gap-4">
              <Link to="/eventos-externos" data-testid="offsite-cta-landing"
                className="group inline-flex items-center gap-3 px-8 py-4 rounded-full bg-[#E2C9A0] text-[#2e5949] hover:bg-[#fdfcf9] transition-colors text-sm uppercase tracking-[0.18em] font-medium">
                {m.home.cta}
                <ArrowRight size={16} className="transition-transform group-hover:translate-x-1" />
              </Link>
              <Link to="/eventos-externos#disponibilitat-externs" data-testid="offsite-cta-calendar"
                className="inline-flex items-center gap-2 text-sm uppercase tracking-[0.18em] text-[#fdfcf9]/85 hover:text-[#E2C9A0] transition-colors">
                <CalendarDays size={16} /> {m.home.ctaSecondary}
              </Link>
            </div>
          </div>
        </div>
      </motion.div>
    </section>
  );
}
